/**
 * By default, Remix will handle hydrating your app on the client for you.
 * You are free to delete this file if you'd like to, but if you ever want it revealed again, you can run `npx remix reveal` ✨
 * For more information, see https://remix.run/file-conventions/entry.client
 */

import { startTransition, StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { HydratedRouter } from 'react-router/dom';

import { keepCriticalCssAlive } from './utils/dev-critical-css-keepalive.client';
import { installDollarSignScrubber } from './utils/strip-dollar-sign.client';

if (ENV.SENTRY_DSN) {
  const { init } = await import('./utils/monitoring.client.ts');
  init({ sentryRelease: ENV.SENTRY_RELEASE });
}

async function prepareApp() {
  if (ENV.MOCK === 'true') {
    const { worker } = await import('mocks/browser');
    return worker.start({ onUnhandledRequest: 'bypass' });
  }

  return Promise.resolve();
}

await prepareApp();

// DEV-ONLY: preserve the SSR critical CSS across React's hydration-time <head> rebuild.
// Must run before hydrateRoot so the critical CSS is captured (and re-applied via
// document.adoptedStyleSheets) before React can remove the original <head>.
//
// TODO: Remove this workaround once we upgrade React 18 -> 19.
// Context: On the RR7 dev server, React 18 hydrates the whole `document`. Vite's dev
// runtime injects each CSS module's styles as `<style>` nodes into the live `<head>`; those
// aren't in React's vdom, so React 18 hits a `<head>` hydration mismatch, silently bails
// (onRecoverableError), and client-renders a brand-new `<head>`. The original head
// (critical CSS + all Vite-injected module styles) is discarded, and Vite never re-injects,
// leaving CSS-module classes unstyled -> content collapses/shifts a beat after load.
// adoptedStyleSheets live outside the DOM tree, so the rebuild can't drop them. React 19's
// native <style>/<link>/<meta> hoisting makes hydration tolerant of these nodes, so the
// rebuild (and this keepalive) is no longer needed. Prod is unaffected (static <link>
// stylesheets, no critical-CSS handoff). See dev-critical-css-keepalive.client.ts.
keepCriticalCssAlive();

// OCGC-3163: remove the stray "$" React's streaming reveal ($RC) writes into a reparented text
// node on prod. See ./utils/strip-dollar-sign.client.ts. Add the `?disableDollarSignScrubber=true`
// query param to the URL to switch the whole workaround off — e.g. when validating a React 19
// upgrade — to verify the issue is gone without it.
installDollarSignScrubber();

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <HydratedRouter />
    </StrictMode>,
  );
});
